import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '@/api/client'
import type { KitchenStation, StationKOT } from '@/types'
import { offlineCaptureAvailable, type OfflineCapability } from './capability'
import { buildOfflineKotSlips, type OfflineKotInput, type OfflineStation } from './buildKotSlip'
import { shouldRelayCounterPrint, watchRelayedPrintJobs } from '@/lib/counterRelay'
import {
  appendLinesToPendingOfflineOrder,
  enqueueOfflineOrder,
  listOfflineOrders,
  pruneSyncedOfflineOrders,
  type OfflineLine,
  type OfflineOrder,
} from './offlineStore'
import { nextTicketRef } from './ticketToken'
import { settleOfflinePayment } from './settleOfflinePayment'
import { readStationCache, writeStationCache } from './stationCache'
import { useOnlineAndReachable } from './reachability'
import { replayOfflineQueue, type ReplaySummary } from './replay'
import { hasSilentPrintShell } from './shell'

/**
 * Everything the counter needs to take an order while the internet is down.
 *
 * Kept out of CounterInterface.tsx deliberately — that file is already ~2,600
 * lines (F-FE-N4), and offline capture is a self-contained concern with its own
 * lifecycle: keep the station routing warm while online, decide honestly whether
 * capture is possible, and drain the queue on reconnect.
 */

/**
 * How often to re-drain while entries remain queued. Deliberately shorter than
 * the per-entry backoff — this is "notice that something is due", not "retry".
 */
export const PENDING_DRAIN_INTERVAL_MS = 20_000

/**
 * How long to wait before re-attempting the station-routing warm-up after a
 * failure (F-FE-N76). Short, because a terminal without it cannot fire offline
 * at all, and the failure window is whatever time remains before the connection
 * drops.
 */
export const STATION_WARM_RETRY_MS = 15_000

/** Flatten the stations payload into the shape the slip builder wants. */
export function toOfflineStations(stations: KitchenStation[]): {
  stations: OfflineStation[]
  categoryToStation: Record<string, string>
  hasKdsStation: boolean
} {
  const active = stations.filter((s) => s.is_active)
  const categoryToStation: Record<string, string> = {}
  for (const s of active) {
    for (const cid of s.category_ids ?? []) categoryToStation[cid] = s.id
  }
  return {
    stations: active.map((s) => ({
      id: s.id,
      name: s.name,
      outputType: s.output_type,
      printLocation: s.print_location ?? 'kitchen',
      printerTarget: s.printer_target ?? '',
    })),
    categoryToStation,
    // A KDS board reads Postgres over SSE, so an offline ticket cannot reach
    // it. Surfaced as a warning, not a refusal — printer stations still work.
    hasKdsStation: active.some((s) => s.output_type === 'kds'),
  }
}

export interface CaptureInput {
  /**
   * Set when appending to an order that is STILL QUEUED on this device
   * (F-FE-N31) — it has no server id yet, so the append is a local mutation of
   * the queued entry and the whole thing replays later as ONE create.
   *
   * Takes precedence over targetOrderId: an order cannot be both.
   */
  heldOpId?: string | null
  /**
   * Set when appending to an order that ALREADY exists on the server
   * (F-CTRL-N37). Null for a brand-new order. Drives whether replay creates an
   * order or appends to one.
   */
  targetOrderId?: string | null
  /**
   * The identifier of the order being appended to, printed on the extra ticket:
   * the held entry's local ticket for an offline order, or the real order number
   * for one the server already has.
   */
  existingTicket?: string
  orderType: string
  tableId: string | null
  tableNumber: string
  serverName: string
  orderTypeLabel: string
  orderNote: string
  userId: string
  /** Guests at the table (F-CTRL-N67). The floor sets it; the counter does not. */
  guestCount?: number
  /** 'server' from the floor screen, otherwise absent. */
  source?: 'pos' | 'server'
  lines: (OfflineLine & { name: string; categoryId: string })[]
}

/**
 * Why this is a discriminated union rather than `CaptureResult | null`: the two
 * ways a capture can fail need completely different handling. "Not ready" means
 * nothing was taken and the cashier must be told this terminal cannot serve
 * offline. "Already synced" means the held order posted while they were
 * composing — the items are still good, they just belong on the real order now.
 * Collapsing both to null produced one vague toast and risked dropping a round.
 */
export type CaptureResult =
  | {
      ok: true
      /**
       * What the kitchen slip says. A locally-minted ticket ("OFF-A3-7") for a
       * new offline order; the real order number when appending to an order the
       * server already has (F-CTRL-N52).
       */
      ticket: string
      slips: StationKOT[]
      /** The updated queue entry, for an append to a held order. Null otherwise. */
      held: OfflineOrder | null
      /**
       * The op id of the entry just queued, for a NEW order (F-CTRL-N55). Null
       * for an append. The caller needs it to attach a tender to this exact
       * entry if the cashier takes payment.
       */
      queuedOpId: string | null
    }
  | { ok: false; reason: 'not_ready' | 'already_synced' }

/**
 * F-CTRL-N52 removed the `todayISO` parameter.
 *
 * It was the business date from GET /day-ops/current, and offline capture keyed
 * its reserved-number pool to it. That coupling is what broke the feature live
 * on 2026-08-26: `['day-ops','current']` is deliberately NOT persisted (it is
 * live data — see PERSIST_PREFIXES in routes/__root.tsx), so a terminal that
 * restarts during an outage reads it as `''`, finds no numbers under that key,
 * and refuses to sell — while holding twenty perfectly good numbers filed under
 * the real date. It was also `''` every morning before the day was opened, which
 * made the pool release and re-reserve itself daily for no reason.
 *
 * Capture now needs no date at all. The ticket is local, and the business day is
 * resolved server-side from `captured_at` at replay — which is where it always
 * belonged, because that is the only place it can be resolved correctly.
 */
export function useOfflineCapture(opts: { online: boolean }) {
  const { online: osOnline } = opts
  // F-FE-N30: subscribe so a reachability flip re-evaluates the predicate
  // immediately. Reading the value once would leave the button stale until some
  // other state change happened to re-render.
  const { backendReachable } = useOnlineAndReachable()
  // Treat the terminal as connected only when BOTH agree.
  const online = osOnline && backendReachable
  const [capability, setCapability] = useState<OfflineCapability>({ ok: false, reason: 'online' })
  const [pendingCount, setPendingCount] = useState(0)
  const [unreconciledCount, setUnreconciledCount] = useState(0)

  const refreshCounts = useCallback(async () => {
    const all = await listOfflineOrders()
    setPendingCount(all.filter((o) => o.status === 'pending').length)
    setUnreconciledCount(all.filter((o) => o.status === 'unreconciled').length)
  }, [])

  /** Recompute the capability predicate from what this terminal actually holds. */
  const refreshCapability = useCallback(async () => {
    const cache = await readStationCache()
    setCapability(
      offlineCaptureAvailable({
        online: osOnline,
        // F-FE-N30: the load-bearing signal. `osOnline` alone stays true when
        // the router is up and the ISP is down — the outage this exists for.
        backendReachable,
        // F-FE-N123: the desktop app OR the Bhookly Server Android app. Both
        // print a station ticket silently; that is the whole requirement.
        canPrintSilently: hasSilentPrintShell(),
        stationsCachedAt: cache?.fetchedAt ?? null,
        hasKdsStation: cache?.hasKdsStation ?? false,
      }),
    )
  }, [osOnline, backendReachable])

  // While online, keep the station routing warm. It is useless to fetch at the
  // moment it is needed, which is precisely when there is no connection.
  //
  // F-CTRL-N52: this used to top up a block of reserved order numbers here too.
  // It no longer does, which removed one of the two things capture could be
  // "unprepared" for.
  //
  // F-FE-N76 fixed the other one, which had the SAME shape and was still here
  // after N52: one attempt, a bare `catch {}`, and no re-run until `online`
  // flipped. So a single failure left the terminal unable to fire offline for
  // the rest of the session, with nothing on screen until the outage arrived.
  //
  // And it failed every time on most tenants, because it called
  // /kitchen/stations — gated on RequireRoles({kitchen, admin}) AND
  // RequireKDSEnabled. A `counter` login got 403; a KDS-disabled venue got 403
  // for everyone. Printer-only venues are exactly what offline capture is for
  // (D1), so the feature was unreachable precisely where it was needed. It now
  // calls the counter-scoped alias and RETRIES until it succeeds.
  useEffect(() => {
    if (!online) {
      void refreshCapability()
      void refreshCounts()
      return
    }
    let cancelled = false
    let timer = 0

    const warm = async () => {
      if (cancelled) return
      let ok = false
      try {
        const res = await apiClient.getCounterStations()
        if (!cancelled && res.data) {
          await writeStationCache(toOfflineStations(res.data), Date.now())
          ok = true
        }
      } catch {
        // Keep the previous cache rather than blanking it: stale routing still
        // reaches a printer, and no routing reaches nothing.
      }
      if (cancelled) return
      await refreshCapability()
      await refreshCounts()
      // Retry until it lands. The terminal is useless offline without this, and
      // the whole point is that it must be ready BEFORE the connection dies.
      if (!ok) timer = window.setTimeout(() => void warm(), STATION_WARM_RETRY_MS)
    }

    void warm()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [online, refreshCapability, refreshCounts])

  /**
   * Take an order offline: mint its ticket, queue it for replay, and build the
   * kitchen slips.
   *
   * Three shapes go through this one path, because everything after the queue
   * write — slip routing, printing, the counts — is identical for all of them:
   *
   *   heldOpId      append to an order still queued on THIS device
   *   targetOrderId append to an order that already exists on the server
   *   neither       a brand-new order — the only shape that mints a ticket
   *
   * Printing is the caller's job; this returns the slips so the caller can
   * decide how to route them.
   */
  const captureOffline = useCallback(
    async (input: CaptureInput): Promise<CaptureResult> => {
      // Read the routing cache FIRST — without it there is no way to know which
      // printer a line belongs to, and there is no point minting a ticket for a
      // slip that cannot be routed.
      const cache = await readStationCache()
      if (!cache) {
        await refreshCapability()
        return { ok: false, reason: 'not_ready' }
      }

      // An append rides on the order it targets, so it does NOT mint a ticket:
      // its identifier is the one already printed on that order's earlier slip,
      // supplied by the caller. Only a brand-new order mints.
      const appending = Boolean(input.heldOpId || input.targetOrderId)
      const ticket = appending
        ? (input.existingTicket ?? '')
        : await nextTicketRef()
      if (!ticket) {
        await refreshCapability()
        return { ok: false, reason: 'not_ready' }
      }

      const capturedAt = Date.now()
      const lines = input.lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
        ...(l.selections && l.selections.length > 0
          ? {
              selections: l.selections.map((sel) => ({
                option_id: sel.option_id,
                quantity: sel.quantity,
              })),
            }
          : {}),
      }))

      let held: OfflineOrder | null = null
      let queuedOpId: string | null = null
      if (input.heldOpId) {
        // F-FE-N31: only safe while the entry is still pending. If replay posted
        // it while the cashier was composing, say so rather than appending to a
        // queue entry that will never be replayed again — those items would
        // never reach a bill.
        held = await appendLinesToPendingOfflineOrder(input.heldOpId, lines)
        if (!held) {
          await refreshCounts()
          return { ok: false, reason: 'already_synced' }
        }
      } else {
        const queued = await enqueueOfflineOrder({
          ticketRef: ticket,
          capturedAt,
          orderType: input.orderType,
          tableId: input.tableId,
          userId: input.userId,
          ...(input.guestCount != null ? { guestCount: input.guestCount } : {}),
          ...(input.source ? { source: input.source } : {}),
          ...(input.targetOrderId ? { targetOrderId: input.targetOrderId } : {}),
          lines,
        })
        queuedOpId = queued.opId
      }

      const kotInput: OfflineKotInput = {
        ticket,
        tableNumber: input.tableNumber,
        serverName: input.serverName,
        orderTypeLabel: input.orderTypeLabel,
        capturedAt,
        orderNote: input.orderNote,
        items: input.lines.map((l) => ({
          name: l.name,
          quantity: l.quantity,
          categoryId: l.categoryId,
          ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
        })),
        stations: cache.stations,
        categoryToStation: cache.categoryToStation,
      }

      // Built from input.lines only — the lines rung on THIS pass. The kitchen
      // already has a ticket for everything queued before it, and reprinting
      // the whole order would read as a second round.
      const slips = buildOfflineKotSlips(kotInput)
      await refreshCapability()
      await refreshCounts()
      return { ok: true, ticket, slips, held, queuedOpId }
    },
    [refreshCapability, refreshCounts],
  )

  /** Drain the queue. Safe to call repeatedly — replay is idempotent server-side. */
  const replayNow = useCallback(async (): Promise<ReplaySummary> => {
    // F-FE-N74: clear entries the PREVIOUS drain synced.
    //
    // pruneSyncedOfflineOrders has existed since 2026-08-05 and was never called
    // once, so every offline order a terminal ever took stayed in IndexedDB for
    // the life of the install — read back in full on every capture, every tray
    // refresh and every count.
    //
    // Pruning at the START of a drain rather than the end is deliberate: an
    // entry synced moments ago may still be the cashier's selected held ticket,
    // and it must survive long enough for captureOffline to notice it is gone
    // and route the round to the real order ('already_synced') instead of
    // dropping it.
    await pruneSyncedOfflineOrders()

    const summary = await replayOfflineQueue({
      replay: async (op, entry) => {
        try {
          // F-CTRL-N37: an operation targeting an existing order is an APPEND,
          // not a new order. It goes through the ordinary add-items endpoint —
          // carrying the op id so a retry cannot add the same food twice — and
          // then fires, which marks the lines sent and puts them on the KDS.
          //
          // The fire's returned slips are deliberately ignored: the ticket
          // already printed locally when the cashier rang it up offline, and
          // printing again would send the kitchen a duplicate.
          if (op.target_order_id) {
            const add = await apiClient.addItemsToOrder(
              op.target_order_id,
              op.lines,
              op.op_id,
            )
            if (!add.success) return { errorCode: add.error ?? 'unknown_error' }
            try {
              // F-FE-N101: relay counter-bound slips on replay, for the same
              // reason the live fire does. The comment above ("already printed
              // locally") holds on the desktop till and nowhere else — a tablet
              // physically cannot print a counter-located slip, so while it was
              // offline that ticket printed NOWHERE. shouldRelayCounterPrint()
              // is !desktop(), which splits those two cases exactly: the desktop
              // still relays nothing and cannot duplicate, and a LAN kitchen
              // station is never counter-bound so it is never relayed either.
              await apiClient.fireKOT(op.target_order_id, undefined, shouldRelayCounterPrint())
            } catch {
              // The items ARE on the order — that is the part that must not be
              // lost. A fire that fails (or finds nothing left to fire on a
              // retry) is recoverable from the counter.
            }
            return { errorCode: null }
          }
          const res = await apiClient.replayOfflineOrder({
            ...op,
            // F-FE-N124: state the relay decision on a CREATE too. The server
            // fires the replayed order itself (F-CTRL-N54), and used to do so
            // with relay hard-coded off — right for the desktop, whose kitchen
            // slip printed during the outage, and wrong for a tablet till,
            // which cannot print a counter-bound slip at all and so printed
            // nothing for that station. Same predicate as every fireKOT call.
            relay_counter_print: shouldRelayCounterPrint(),
          })
          if (!res.success) return { errorCode: res.error ?? 'unknown_error' }
          // Narrate the relayed jobs, if any, so the operator hears whether the
          // counter actually printed. Empty on the desktop; returns at once.
          void watchRelayedPrintJobs(res.data?.relayed_print_jobs)
          // F-CTRL-N54: synced, but the server could not mark it fired. Report
          // it so the counter can say so rather than letting the cashier find
          // out when the next fire reprints an already-cooked round.
          const needsFire = res.data?.fired === false

          // F-CTRL-N55: the order exists; now settle the money that was taken
          // during the outage.
          const needsAttention = entry.payment
            ? await settleOfflinePayment(entry, res.data?.order_id, res.data?.total_amount)
            : false

          return { errorCode: null, needsFire, needsAttention }
        } catch (e) {
          // The API client throws on non-2xx; the stable code is what the
          // classifier keys off, so dig it out rather than flattening to
          // "network error" and retrying a permanent conflict forever.
          const code = (e as { code?: string; error?: string })?.code
            ?? (e as { error?: string })?.error
            ?? 'network_error'
          return { errorCode: code }
        }
      },
      report: async (op, failureCode) => {
        try {
          const res = await apiClient.reportUnreconciledOffline({
            op_id: op.opId,
            // Whatever the KITCHEN SLIP says — the local ticket, or a legacy
            // reserved number for an entry queued by a pre-N52 bundle. This
            // column is display-only, and its whole job is letting a manager
            // match the paper in the pass to the row they are deciding about.
            order_number: op.ticketRef || op.orderNumber || '',
            target_order_id: op.targetOrderId ?? null,
            captured_at: op.capturedAt,
            failure_code: failureCode,
            ...(op.payment ? { collected_amount: op.payment.amount } : {}),
            lines: op.lines,
          })
          return res.success === true
        } catch {
          // Leave it pending — the next drain retries. Never swallow.
          return false
        }
      },
    })
    await refreshCounts()
    await refreshCapability()
    return summary
  }, [refreshCounts, refreshCapability])

  // Drain automatically the moment the connection returns.
  const prevOnline = useRef(online)
  const onReplayRef = useRef<((s: ReplaySummary) => void) | null>(null)
  useEffect(() => {
    const was = prevOnline.current
    prevOnline.current = online
    if (!was && online) {
      void replayNow().then((s) => onReplayRef.current?.(s))
    }
  }, [online, replayNow])

  /**
   * Keep draining while anything is still queued (F-FE-N75).
   *
   * The edge above was the ONLY thing that ever ran a drain. So a queue that
   * survived its first attempt — most commonly `day_not_open`, which clears the
   * moment a manager opens the day at the counter — sat there until the
   * connection happened to drop and return again. Nothing on screen said so, and
   * the orders in it are food the kitchen has already cooked.
   *
   * The interval is short because the entries themselves are backed off
   * (retryBackoffMs): this decides how promptly a DUE entry is noticed, not how
   * often a failing one is hammered. Stops as soon as the queue is empty, so a
   * terminal in normal service does no work here at all.
   */
  useEffect(() => {
    if (!online || pendingCount === 0) return
    const id = window.setInterval(() => {
      void replayNow().then((s) => onReplayRef.current?.(s))
    }, PENDING_DRAIN_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [online, pendingCount, replayNow])

  /** Register a callback for the automatic post-reconnect drain. */
  const onReplayComplete = useCallback((fn: (s: ReplaySummary) => void) => {
    onReplayRef.current = fn
  }, [])

  return {
    capability,
    pendingCount,
    unreconciledCount,
    captureOffline,
    replayNow,
    onReplayComplete,
    refreshCounts,
  }
}
